import { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import * as api from "@/lib/api";
import OfferForm, { OFFER_TYPES } from "@/components/lab/OfferForm";
import { Tag, Plus, Copy, Send, Pencil, Power, Trash2, ExternalLink, Share2, Loader2, UserPlus, X } from "lucide-react";

const STATUS = {
  attiva: ["ATTIVA", "bg-primary/15 text-primary"],
  in_scadenza: ["IN SCADENZA", "bg-[hsl(var(--c-fat))]/20 text-[hsl(var(--c-fat))]"],
  scaduta: ["SCADUTA", "bg-[hsl(0_72%_60%)]/20 text-[hsl(0_72%_68%)]"],
  disattivata: ["DISATTIVATA", "bg-white/10 text-muted-foreground"],
};

const timeLeft = (iso) => {
  const s = Math.floor((new Date(iso) - Date.now()) / 1000);
  if (s <= 0) return "scaduta";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d}g ${h}h` : `${h}h ${m}m`;
};

const IconBtn = ({ onClick, title, testid, className = "", children }) => (
  <button type="button" onClick={onClick} title={title} data-testid={testid}
    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-muted-foreground transition-colors hover:text-primary ${className}`}>{children}</button>
);

function SendPanel({ o, patients, onClose }) {
  const [sent, setSent] = useState(null);
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { setSent(new Set((await api.listOfferSends(o.id)).map((x) => x.patient_id))); } catch (e) { setSent(new Set()); } }, [o.id]);
  useEffect(() => { load(); }, [load]);
  const name = (p) => [p.cognome, p.nome].filter(Boolean).join(" ") || "Senza nome";
  const list = patients.filter((p) => name(p).toLowerCase().includes(q.toLowerCase()));
  const send = async () => {
    setBusy(true);
    try { const r = await api.sendOffer(o.id, sel); toast.success(`Offerta inviata a ${r.sent} pazienti`); setSel([]); load(); }
    catch (e) { toast.error("Invio non riuscito"); } finally { setBusy(false); }
  };
  const unsend = async (pid) => { try { await api.unsendOffer(o.id, pid); load(); } catch (e) { toast.error("Errore"); } };
  return (
    <div className="space-y-2 border-t border-white/5 bg-black/30 p-3" data-testid={`offer-send-panel-${o.id}`}>
      <div className="flex items-center justify-between text-xs font-semibold"><span>Invia al link personale dei pazienti</span><button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid={`offer-send-close-${o.id}`}><X className="h-3.5 w-3.5" /></button></div>
      <input className="lab-input py-1.5 text-xs" placeholder="Cerca paziente…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`offer-send-search-${o.id}`} />
      <div className="max-h-48 space-y-0.5 overflow-y-auto">
        {!sent ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : list.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-2 rounded px-1.5 py-1 text-xs hover:bg-white/5">
            {sent.has(p.id) ? (
              <><span className="truncate">{name(p)} <span className="ml-1 rounded bg-[hsl(43_74%_58%)]/20 px-1 text-[9px] font-bold text-[hsl(43_74%_62%)]">INVIATA</span></span>
              <button onClick={() => unsend(p.id)} className="text-[10px] text-muted-foreground hover:text-destructive" data-testid={`offer-unsend-${o.id}-${p.id}`}>Ritira</button></>
            ) : (
              <label className="flex flex-1 cursor-pointer items-center gap-2"><input type="checkbox" checked={sel.includes(p.id)} onChange={(e) => setSel((s) => (e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id)))} data-testid={`offer-send-check-${o.id}-${p.id}`} /><span className="truncate">{name(p)}</span></label>
            )}
          </div>
        ))}
      </div>
      <button onClick={send} disabled={!sel.length || busy} className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[hsl(43_74%_58%)] px-3 py-2 text-xs font-bold text-black disabled:opacity-40" data-testid={`offer-send-submit-${o.id}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Invia a {sel.length} pazienti
      </button>
      <p className="text-[10px] text-muted-foreground">Il paziente vedrà la card OFFERTE evidenziata in oro; il timer parte quando apre l'offerta (durata: {o.duration === "custom" ? "personalizzata" : o.duration}).</p>
    </div>
  );
}

function OfferCard({ o, onEdit, onToggle, onDelete, patients }) {
  const [sendOpen, setSendOpen] = useState(false);
  const url = api.offerPublicUrl(o.slug);
  const [label, cls] = STATUS[o.status] || STATUS.attiva;
  const typeLabel = OFFER_TYPES.find((t) => t[0] === o.type)?.[1];
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copiato"); } catch (e) { toast.error("Copia non riuscita"); } };
  const tg = () => window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(o.title)}`, "_blank", "noopener");
  const share = async () => {
    if (!navigator.share) { copy(); return; }
    try { await navigator.share({ title: o.title, text: o.subtitle || o.title, url }); } catch (e) { if (e?.name !== "AbortError") toast.error("Condivisione non riuscita"); }
  };
  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-black/20" data-testid={`offer-card-${o.id}`}>
      <div className="flex gap-3 p-3">
        <div className="h-20 w-16 shrink-0 overflow-hidden rounded-lg bg-black/40">
          {o.image_path && <img src={api.offerImageUrl(o.image_path)} alt="" className="h-full w-full object-cover" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold tracking-wider ${cls}`} data-testid={`offer-status-${o.id}`}>{label}</span>
            {typeLabel && <span className="truncate text-[10px] text-muted-foreground">{typeLabel}</span>}
          </div>
          <div className="mt-1 truncate font-display text-sm font-bold" data-testid={`offer-title-${o.id}`}>{o.title}</div>
          <div className="text-[11px] text-muted-foreground" data-testid={`offer-expiry-${o.id}`}>
            Scade {new Date(o.expires_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })} · {timeLeft(o.expires_at)}
          </div>
          {o.price_promo != null && <div className="mt-0.5 text-xs font-semibold text-[hsl(var(--c-fat))]">{o.price_original != null && <span className="mr-1.5 text-muted-foreground line-through">€ {o.price_original}</span>}€ {o.price_promo}</div>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 border-t border-white/5 px-3 py-2">
        <button onClick={copy} className="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground" data-testid={`offer-copy-${o.id}`}><Copy className="h-3.5 w-3.5" /> Copia link</button>
        <button onClick={tg} className="inline-flex items-center gap-1 rounded-lg border border-[hsl(199_89%_55%)]/50 bg-[hsl(199_89%_55%)]/15 px-2.5 py-1.5 text-xs font-semibold text-[hsl(199_89%_68%)]" data-testid={`offer-telegram-${o.id}`}><Send className="h-3.5 w-3.5" /> Telegram</button>
        <IconBtn onClick={share} title="Condividi" testid={`offer-share-${o.id}`}><Share2 className="h-3.5 w-3.5" /></IconBtn>
        <a href={url} target="_blank" rel="noreferrer" title="Apri landing" data-testid={`offer-open-${o.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-muted-foreground transition-colors hover:text-primary"><ExternalLink className="h-3.5 w-3.5" /></a>
        <IconBtn onClick={() => setSendOpen((v) => !v)} title="Invia a paziente" testid={`offer-send-${o.id}`} className="text-[hsl(43_74%_62%)]"><UserPlus className="h-3.5 w-3.5" /></IconBtn>
        <div className="ml-auto flex gap-1.5">
          <IconBtn onClick={() => onEdit(o)} title="Modifica" testid={`offer-edit-${o.id}`}><Pencil className="h-3.5 w-3.5" /></IconBtn>
          <IconBtn onClick={() => onToggle(o)} title={o.active ? "Disattiva" : "Riattiva"} testid={`offer-toggle-${o.id}`} className={o.active ? "" : "text-primary"}><Power className="h-3.5 w-3.5" /></IconBtn>
          <IconBtn onClick={() => onDelete(o)} title="Elimina" testid={`offer-delete-${o.id}`} className="hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></IconBtn>
        </div>
      </div>
      {sendOpen && <SendPanel o={o} patients={patients} onClose={() => setSendOpen(false)} />}
    </div>
  );
}

export default function OfferteDrawer({ open, setOpen, patients = [] }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setOffers(await api.listOffers()); } catch (e) { console.error("Errore offerte:", e); toast.error("Impossibile caricare le offerte"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) { refresh(); setEditing(null); } }, [open, refresh]);

  const onDone = async (saved, isNew) => {
    setEditing(null);
    await refresh();
    if (isNew) {
      try { await navigator.clipboard.writeText(api.offerPublicUrl(saved.slug)); toast.success("Link pubblico copiato negli appunti"); } catch (e) { console.error("Clipboard:", e); }
    }
  };
  const onToggle = async (o) => {
    try { await api.setOfferActive(o.id, !o.active); toast.success(o.active ? "Offerta disattivata" : "Offerta riattivata"); refresh(); } catch (e) { toast.error("Errore"); }
  };
  const onDelete = async (o) => {
    if (!window.confirm(`Eliminare definitivamente "${o.title}"? Il link smetterà di funzionare.`)) return;
    try { await api.deleteOffer(o.id); toast.success("Offerta eliminata"); refresh(); } catch (e) { toast.error("Errore eliminazione"); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full border-white/10 bg-[hsl(var(--card))] text-foreground sm:max-w-md" data-testid="offerte-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display text-foreground"><Tag className="h-5 w-5 text-primary" /> Offerte</SheetTitle>
          <SheetDescription className="sr-only">Crea e gestisci le promozioni con link pubblico</SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-4 overflow-y-auto pb-6 pr-1" style={{ maxHeight: "calc(100vh - 110px)" }}>
          {editing ? (
            <OfferForm offer={editing === "new" ? null : editing} onDone={onDone} onCancel={() => setEditing(null)} />
          ) : (
            <>
              <button onClick={() => setEditing("new")} className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm font-bold text-primary transition-colors hover:bg-primary/20" data-testid="new-offer-btn">
                <Plus className="h-4 w-4" /> NUOVA OFFERTA
              </button>
              {loading && offers.length === 0 ? (
                <div className="flex justify-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : offers.length === 0 ? (
                <div className="rounded-lg border border-dashed border-white/10 py-10 text-center text-sm text-muted-foreground" data-testid="offers-empty">Nessuna offerta creata.</div>
              ) : (
                <div className="space-y-3" data-testid="offers-list">
                  {offers.map((o) => <OfferCard key={o.id} o={o} patients={patients} onEdit={setEditing} onToggle={onToggle} onDelete={onDelete} />)}
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
